import { useEffect, useState } from 'react';

import { Modal } from '@/components/Modal';
import {
  EDUCATION_LEVEL_LABELS,
  EDUCATION_LEVELS,
  schoolLabel,
  useEducations,
  useSaveEducations,
  useSchoolSearch,
  type EducationRow,
  type EducationView,
} from '@/pages/hr/education/educationApi';

/**
 * 학력 — <b>한 줄</b>, 고치는 곳은 <b>모달 하나</b>(사용자 지시 2026-11-02: 「수정, 검색 뜨지 말고 모달 한 번에」).
 *
 * 구분 하나 + 학교 이름 하나 + 공개 여부. 최종학력과 학력을 따로 두지 않는다 — 이 한 줄이 곧 최종학력이다.
 * 학교는 모달 안의 검색 칸에 치면 바로 아래 목록이 뜨고, 목록에 없는 이름은 친 그대로 저장된다.
 * 서버는 줄 목록을 받지만 여기서는 늘 한 줄만 보낸다(옛 자료에 줄이 여럿이면 가장 높은 줄을 보여 주고, 저장하면 한 줄이 된다).
 *
 * 마이페이지 「내 정보」 표({@code MyEducationRow})와 인사자료 사람 창({@link EducationSummary})이 같이 쓴다.
 */

/** 인사자료 사람 창의 「학력」 탭 — 한 줄 + [수정]. */
export function EducationSummary({ personnelId, canEdit }: { personnelId: string; canEdit: boolean }) {
  const view = useEducations(personnelId);
  const [editing, setEditing] = useState(false);
  const top = view.data ? topRow(view.data) : null;

  return (
    <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: 13 }}>
      <b style={{ color: 'var(--text-primary)' }}>{educationText(view.data) || '-'}</b>
      {view.data && top && (
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{view.data.open ? '공개' : '비공개'}</span>
      )}
      {top?.selfEntered && <span className="badge badge-yellow">본인 입력</span>}
      {canEdit && (
        <button type="button" className="btn-secondary" style={{ fontSize: 12, padding: '3px 10px', marginLeft: 'auto' }} onClick={() => setEditing(true)}>
          수정
        </button>
      )}
      {editing && <EducationModal personnelId={personnelId} onClose={() => setEditing(false)} />}
    </div>
  );
}

/** @param personnelId null 이면 본인(마이페이지) */
export function EducationModal({ personnelId, onClose }: { personnelId: string | null; onClose: () => void }) {
  const view = useEducations(personnelId);
  const save = useSaveEducations(personnelId);
  const [level, setLevel] = useState('대졸');
  const [school, setSchool] = useState('');
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!view.data || loaded) return;
    const top = topRow(view.data);
    setLevel(top?.level ?? '대졸');
    setSchool(top?.schoolName ?? '');
    setOpen(view.data.open);
    setLoaded(true);
  }, [view.data, loaded]);

  // 검색은 친 글자를 잠깐 기다렸다 묻는다 — 한 글자마다 서버를 부르지 않는다.
  const [needle, setNeedle] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setNeedle(school), 200);
    return () => window.clearTimeout(timer);
  }, [school]);
  const schools = useSchoolSearch(needle);
  const typed = school.trim();
  const results = (schools.data ?? []).filter((item) => schoolLabel(item) !== typed);

  const highSchool = level === '고졸';
  const submit = () => {
    if (!view.data) return;
    setError(null);
    const row: EducationRow = {
      id: topRow(view.data)?.id ?? null,
      level,
      schoolName: highSchool ? null : typed || null,
      major: null,
      graduationYear: null,
      selfEntered: false,
    };
    save.mutate(
      { rows: [row], open },
      {
        onSuccess: onClose,
        onError: (cause) => setError(cause instanceof Error ? cause.message : '저장하지 못했습니다.'),
      },
    );
  };

  return (
    <Modal
      title="학력"
      onClose={onClose}
      maxWidth="520px"
      actions={
        <button
          type="button"
          className="btn-primary"
          style={{ fontSize: 12, padding: '5px 14px' }}
          disabled={!view.data || save.isPending}
          onClick={submit}
        >
          {save.isPending ? '저장 중' : '저장'}
        </button>
      }
    >
      {!view.data ? (
        <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>불러오는 중입니다.</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-1">
            {EDUCATION_LEVELS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setLevel(value)}
                className={level === value ? 'btn-primary' : 'btn-secondary'}
                style={{ fontSize: 13, padding: '6px 14px' }}
              >
                {EDUCATION_LEVEL_LABELS[value]}
              </button>
            ))}
          </div>

          {!highSchool && (
            <div className="space-y-1">
              <input
                className="input-field"
                style={{ width: '100%', fontSize: 14 }}
                placeholder="학교 이름"
                value={school}
                onChange={(event) => setSchool(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') submit();
                }}
              />
              {results.length > 0 && (
                <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
                  {results.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSchool(schoolLabel(item))}
                      className="w-full flex items-center gap-2 hover:bg-slate-50"
                      style={{ textAlign: 'left', padding: '9px 12px', borderBottom: '1px solid var(--border)', fontSize: 13 }}
                    >
                      <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{schoolLabel(item)}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
                        {[item.kind, item.region].filter(Boolean).join(' · ')}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex">
            {[false, true].map((value) => (
              <button
                key={String(value)}
                type="button"
                onClick={() => setOpen(value)}
                className={open === value ? 'btn-primary' : 'btn-secondary'}
                style={{ fontSize: 12, padding: '4px 12px', borderRadius: value ? '0 8px 8px 0' : '8px 0 0 8px' }}
              >
                {value ? '공개' : '비공개'}
              </button>
            ))}
          </div>

          {error && <p style={{ fontSize: 12, color: 'var(--danger)' }}>{error}</p>}
        </div>
      )}
    </Modal>
  );
}

/** 보여 줄 한 줄 — 옛 자료에 여럿이면 가장 높은 단계. */
export function topRow(view: EducationView): EducationRow | null {
  const rank = (level: string) => (EDUCATION_LEVELS as readonly string[]).indexOf(level);
  let top: EducationRow | null = null;
  for (const row of view.rows) {
    if (!top || rank(row.level) > rank(top.level)) top = row;
  }
  return top;
}

/** 「한양대학교 (대졸)」 · 「고졸」. */
export function educationText(view: EducationView | undefined): string {
  const top = view ? topRow(view) : null;
  if (!top) return '';
  return top.schoolName ? `${top.schoolName} (${top.level})` : top.level;
}
